import React, { Component } from "react";
import { Input, Spin, Alert, Descriptions, message, Select } from "antd";
import { KeyOutlined, WifiOutlined, LockOutlined } from "@ant-design/icons";
import { connect } from "react-redux";
import axios from "axios";
import { ModalItem, ButtonItem, InputNumberItem, SelectItem, TextAreaItem, SwitchItem } from "../Items";
import TabbedDynamicTable from "../TabbedDynamicTable";
import { etiquetaPorPermisoId, definicionPorPermisoId } from "../../Utils/autorizaciones";

// Modal de autorización rápida. Dos modos:
// - "Código de autorizador": un autorizador presente teclea su PIN de 6 dígitos.
// - "Solicitud remota": se notifica (push + campana) a los autorizadores y se
//   espera su respuesta en vivo (evento WS "autorizacion_update" reenviado por
// Cerrar el modal por cualquier vía cancela la solicitud pendiente.
class ModalAutorizacion extends Component {
  state = {
    pin: "",
    loading: false,
    solicitudId: null,
    esperando: false,
    valorSolicitado: null,
    valorSolicitadoTexto: "",
    accesoLibre: false,
    comentario: "",
    autorizadoresDisponibles: [],
    autorizadoresSeleccionados: [],
    cargandoAutorizadores: false,
  };

  componentDidMount() {
    window.addEventListener("autorizacion_update", this.onAutorizacionUpdate);
  }

  componentWillUnmount() {
    window.removeEventListener("autorizacion_update", this.onAutorizacionUpdate);
    this.detenerPolling();
  }

  componentDidUpdate(prevProps) {
    // Cualquier cierre del modal (X, mask, Escape o el padre) cancela la
    // solicitud que siga pendiente.
    if (prevProps.visible && !this.props.visible) {
      if (this.state.esperando && this.state.solicitudId) {
        this.cancelarSolicitud();
      } else {
        this.reiniciar();
      }
    }
    if (!prevProps.visible && this.props.visible) {
      this.cargarAutorizadoresDisponibles();
    }
  }

  reiniciar = () => {
    this.detenerPolling();
    this.setState({
      pin: "",
      loading: false,
      solicitudId: null,
      esperando: false,
      valorSolicitado: null,
      valorSolicitadoTexto: "",
      accesoLibre: false,
      comentario: "",
      autorizadoresSeleccionados: [],
    });
  };

  // Lista de autorizadores configurados y con PIN 
  // para que el solicitante elija a quién enviar 
  cargarAutorizadoresDisponibles = async () => {
    this.setState({ cargandoAutorizadores: true });
    try {
      const res = await axios.post(
        "/autorizaciones/autorizadoresDelPermiso",
        { empresaId: this.props.empresaId, permisoId: this.props.permisoId },
        { headers: this.props.headersToken }
      );
      if (res.data.success === 2) {
        this.setState({ autorizadoresDisponibles: res.data.autorizadores || [] });
      }
    } catch (error) {
      // El selector queda vacío y se notifica a todos 
    } finally {
      this.setState({ cargandoAutorizadores: false });
    }
  };

  tipoValorActual = () => {
    const definicion = definicionPorPermisoId(this.props.permisoId);
    if (definicion?.tipoValor === "moneda|porcentaje") {
      return this.props.contexto?.tipoDescuento === "cantidad" ? "moneda" : "porcentaje";
    }
    return definicion?.tipoValor;
  };

  validarValor = () => {
    if (!this.tipoValorActual() || this.state.accesoLibre) return true;
    const { valorSolicitado } = this.state;
    if (valorSolicitado === null || valorSolicitado === undefined || valorSolicitado === "") {
      message.warning("Indica el valor que necesitas aplicar, o marca \"Acceso libre\"");
      return false;
    }
    return true;
  };

  contextoConValor = () => {
    const { valorSolicitado, valorSolicitadoTexto, accesoLibre } = this.state;
    return {
      ...this.props.contexto,
      valorActual: this.props.contexto?.valorActual,
      valorSolicitado: accesoLibre ? undefined : valorSolicitado,
      valorSolicitadoTexto: accesoLibre ? undefined : (valorSolicitadoTexto || undefined),
      accesoLibre,
    };
  };

  autorizarConPin = async () => {
    if (!this.validarValor()) return;
    const { pin, valorSolicitado, accesoLibre } = this.state;
    if (!/^\d{6}$/.test(pin)) {
      message.warning("El código debe ser de 6 dígitos");
      return;
    }
    this.setState({ loading: true });
    try {
      const res = await axios.post(
        "/autorizaciones/autorizarConPin",
        {
          empresaId: this.props.empresaId,
          pin,
          permisoId: this.props.permisoId,
          contexto: this.contextoConValor(),
        },
        { headers: this.props.headersToken }
      );
      if (res.data.success === 2) {
        message.success(`Autorizado por ${res.data.autorizador?.nombre || "autorizador"}`);
        this.props.onAutorizado({
          autorizacionId: res.data.autorizacionId,
          tokenConsumo: res.data.tokenConsumo,
          autorizador: res.data.autorizador,
          valorSolicitado: accesoLibre ? undefined : valorSolicitado,
          accesoLibre,
        });
      }
    } catch (error) {
      message.error(error?.response?.data?.mensaje || "No se pudo autorizar");
    } finally {
      this.setState({ loading: false, pin: "" });
    }
  };

  // -------------------------------------------------------- modo remoto --

  crearSolicitud = async () => {
    if (!this.validarValor()) return;
    const { comentario, autorizadoresSeleccionados } = this.state;
    this.setState({ loading: true });
    try {
      const res = await axios.post(
        "/autorizaciones/crearSolicitud",
        {
          empresaId: this.props.empresaId,
          permisoId: this.props.permisoId,
          contexto: { ...this.contextoConValor(), comentario: comentario || undefined },
          autorizadoresSeleccionados:
            autorizadoresSeleccionados.length > 0 ? autorizadoresSeleccionados : undefined,
        },
        { headers: this.props.headersToken }
      );
      if (res.data.success === 2) {
        this.setState({ solicitudId: res.data.solicitudId, esperando: true });
        this.iniciarPolling(res.data.solicitudId);
      }
    } catch (error) {
      message.error(error?.response?.data?.mensaje || "No se pudo enviar la solicitud");
    } finally {
      this.setState({ loading: false });
    }
  };

  cancelarSolicitud = async () => {
    const { solicitudId } = this.state;
    this.reiniciar();
    if (!solicitudId) return;
    try {
      await axios.post(
        "/autorizaciones/cancelarSolicitud",
        { empresaId: this.props.empresaId, solicitudId },
        { headers: this.props.headersToken }
      );
    } catch (error) {
      // Puede haberse resuelto justo antes de cancelar — no es un error de UI
    }
  };

  onAutorizacionUpdate = (event) => {
    const data = event.detail;
    if (!this.state.esperando || data?.solicitudId !== this.state.solicitudId) return;
    this.procesarResolucion(data);
  };

  procesarResolucion = (data) => {
    if (data.estado === "APROBADA" && data.tokenConsumo) {
      const { solicitudId, valorSolicitado, accesoLibre } = this.state;
      this.reiniciar();
      message.success(`Autorizado por ${data.autorizadorNombre || "autorizador"}`);
      this.props.onAutorizado({
        autorizacionId: solicitudId,
        tokenConsumo: data.tokenConsumo,
        autorizador: { nombre: data.autorizadorNombre },
        valorSolicitado: accesoLibre ? undefined : valorSolicitado,
        accesoLibre,
      });
    } else if (["RECHAZADA", "EXPIRADA", "CANCELADA"].includes(data.estado)) {
      this.reiniciar();
      if (data.estado === "RECHAZADA") {
        message.error(
          `Solicitud rechazada${data.autorizadorNombre ? ` por ${data.autorizadorNombre}` : ""}${
            data.motivoRechazo ? `: ${data.motivoRechazo}` : ""
          }`
        );
      } else {
        message.warning("La solicitud expiró sin respuesta");
      }
    }
  };

  // Respaldo por si el WebSocket está caído
  iniciarPolling = (solicitudId) => {
    this.detenerPolling();
    this.pollingInterval = setInterval(async () => {
      try {
        const res = await axios.post(
          "/autorizaciones/selectSolicitud",
          { empresaId: this.props.empresaId, solicitudId },
          { headers: this.props.headersToken }
        );
        const solicitud = res.data?.solicitud;
        if (solicitud && solicitud.estado !== "PENDIENTE") {
          this.procesarResolucion({
            solicitudId,
            estado: solicitud.estado,
            tokenConsumo: solicitud.tokenConsumo,
            autorizadorNombre: solicitud.autorizadorNombre,
            motivoRechazo: solicitud.motivoRechazo,
          });
        }
      } catch (error) {
        // Silencioso: es solo respaldo
      }
    }, 10000);
  };

  detenerPolling = () => {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  };


  onCancelModal = () => {
    // La cancelación de la solicitud pendiente ocurre en componentDidUpdate
    // al pasar visible true→false, cubriendo también cierres desde el padre.
    this.props.onCancel();
  };

  renderContexto() {
    const contexto = this.props.contexto || {};
    const hayValorActual =
      contexto.valorActual !== undefined && contexto.valorActual !== null && contexto.valorActual !== "";
    return (
      <Descriptions size="small" column={1} bordered style={{ marginBottom: 12 }}>
        <Descriptions.Item label="Operación">
          {etiquetaPorPermisoId(this.props.permisoId)}
        </Descriptions.Item>
        {contexto.insumoNombre && (
          <Descriptions.Item label="Insumo">{contexto.insumoNombre}</Descriptions.Item>
        )}
        {contexto.descripcion && (
          <Descriptions.Item label="Detalle">{contexto.descripcion}</Descriptions.Item>
        )}
        {hayValorActual && (
          <Descriptions.Item label="Valor actual">{String(contexto.valorActual)}</Descriptions.Item>
        )}
      </Descriptions>
    );
  }

  renderSeccionValor() {
    const tipoValor = this.tipoValorActual();
    if (!tipoValor) return null;
    const { accesoLibre, valorSolicitado } = this.state;
    return (
      <div style={{ marginBottom: 12 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 8,
          }}
        >
          <span>¿Qué valor necesitas aplicar?</span>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <SwitchItem
              size="small"
              checked={accesoLibre}
              onChange={(checked) =>
                this.setState({ accesoLibre: checked, valorSolicitado: null, valorSolicitadoTexto: "" })
              }
            />
            <span style={{ fontSize: 12, color: "#888" }}>Acceso libre</span>
          </div>
        </div>
        {accesoLibre ? (
          <Alert
            type="warning"
            showIcon
            message="El acceso libre siempre pedirá el código del autorizador al aprobarse, sin importar la configuración de este permiso."
          />
        ) : tipoValor === "seleccion" ? (
          <SelectItem
            placeholder="Selecciona el valor a aplicar"
            value={valorSolicitado || undefined}
            style={{ width: "100%" }}
            onChange={(value, option) =>
              this.setState({
                valorSolicitado: value,
                valorSolicitadoTexto: option?.children || "",
              })
            }
          >
            {this.props.opcionesValor}
          </SelectItem>
        ) : (
          <InputNumberItem
            placeholder={tipoValor === "moneda" ? "Valor ($)" : "Valor (%)"}
            dinero={tipoValor === "moneda"}
            porcentaje={tipoValor === "porcentaje"}
            max={tipoValor === "porcentaje" ? 100 : undefined}
            value={valorSolicitado}
            onChange={(value) => this.setState({ valorSolicitado: value })}
            style={{ width: "100%" }}
          />
        )}
      </div>
    );
  }

  renderTabPin() {
    const { pin, loading, esperando } = this.state;
    return (
      <div>
        <p>Un autorizador presente debe ingresar su código personal de 6 dígitos:</p>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <Input.Password
            value={pin}
            onChange={(e) => this.setState({ pin: e.target.value.replace(/\D/g, "").slice(0, 6) })}
            placeholder="••••••"
            maxLength={6}
            autoComplete="off"
            disabled={esperando}
            style={{ width: 180, letterSpacing: 6, textAlign: "center" }}
            onPressEnter={this.autorizarConPin}
          />
          <ButtonItem verde loading={loading} disabled={esperando} onClick={this.autorizarConPin}>
            Autorizar
          </ButtonItem>
        </div>
      </div>
    );
  }

  renderTabRemota() {
    const {
      loading,
      esperando,
      comentario,
      autorizadoresDisponibles,
      autorizadoresSeleccionados,
      cargandoAutorizadores,
    } = this.state;

    if (esperando) {
      return (
        <div style={{ textAlign: "center", padding: 16 }}>
          <Spin />
          <p style={{ marginTop: 12 }}>
            Esperando respuesta de los autorizadores…
            <br />
            Recibirán una notificación en su app móvil y en el ERP.
          </p>
          <ButtonItem rojo onClick={this.cancelarSolicitud}>
            Cancelar solicitud
          </ButtonItem>
        </div>
      );
    }

    return (
      <div style={{ paddingBottom: 12 }}>
        <Alert
          type="info"
          showIcon
          message="Se enviará una notificación a los autorizadores elegidos (o a todos los configurados, si no eliges ninguno); podrán aprobar o rechazar desde su teléfono o el ERP."
          style={{ marginBottom: 12 }}
        />
        <div style={{ marginBottom: 12 }}>
          <p style={{ marginBottom: 4 }}>¿A quién enviar la solicitud? (opcional)</p>
          <SelectItem
            mode="multiple"
            placeholder="Todos los autorizadores configurados"
            value={autorizadoresSeleccionados}
            loading={cargandoAutorizadores}
            style={{ width: "100%" }}
            onChange={(valores) => this.setState({ autorizadoresSeleccionados: valores })}
          >
            {autorizadoresDisponibles.map((u) => (
              <Select.Option key={u._id} value={u._id.toString()}>
                {`${u.nombre} ${u.apellidoPaterno || ""}`}
              </Select.Option>
            ))}
          </SelectItem>
        </div>
        <TextAreaItem
          placeholder="Comentario para el autorizador (opcional)"
          value={comentario}
          onChange={(e) => this.setState({ comentario: e.target.value })}
          rows={2}
          style={{ marginBottom: 12 }}
        />
        <ButtonItem type="primary" loading={loading} onClick={this.crearSolicitud} block>
          Enviar solicitud
        </ButtonItem>
      </div>
    );
  }

  render() {
    const { visible } = this.props;
    const { esperando } = this.state;

    const tabs = [
      {
        key: "pin",
        label: (
          <span>
            <KeyOutlined /> Código de autorizador
          </span>
        ),
        customContent: this.renderTabPin(),
      },
      {
        key: "remota",
        label: (
          <span>
            <WifiOutlined /> Solicitud remota
          </span>
        ),
        customContent: this.renderTabRemota(),
      },
    ];

    return (
      <ModalItem
        title={
          <span>
            <LockOutlined style={{ color: "#fa8c16", marginRight: 8 }} />
            Autorización requerida
          </span>
        }
        visible={visible}
        onCancel={this.onCancelModal}
        footer={null}
        destroyOnClose
        width={520}
        spinning={false}
        paddingTop
      >
        {this.renderContexto()}
        {!esperando && this.renderSeccionValor()}
        <TabbedDynamicTable tabs={tabs} defaultActiveKey="pin" />
      </ModalItem>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
});

export default connect(mapStateToProps, {})(ModalAutorizacion);
